import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { formatDistance } from 'date-fns';
import { CompanyService } from 'src/core/services/company.service';
import { LoaderService } from 'src/core/services/loader.service';
import { SharedService } from 'src/core/services/shared.service';
import { Company, CompanyType } from 'src/interfaces/company';

interface DialogData {
  title: string;
  message: string;
  types: string | null;
}

interface SelectableCompany {
  selected: boolean;
  company: Company;
}

@Component({
  selector: 'app-company-search-dialog',
  templateUrl: './company-search-dialog.component.html',
  styleUrls: ['./company-search-dialog.component.scss']
})
export class CompanySearchDialogComponent implements OnInit {

  displayedColumns: string[] = [
    'name',
    'type',
    'email',
    'phone_number',
    'created_at',
    'selected'
  ];

  companyTypes: CompanyType[] = [];

  isLoading: boolean = false;
  initialized: boolean = false;
  companiesDataSource: MatTableDataSource<SelectableCompany>;
  selectedCompany: Company | null = null;
  page: number = 1;
  limit: number = 5;
  totalNumberOfCompanies: number = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  companyNameFilter = this._formBuilder.control([''])

  constructor(
    public _dialogRef: MatDialogRef<CompanySearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _companyService: CompanyService,
    private _formBuilder: FormBuilder,
    private _sharedService: SharedService,
    private _loaderService: LoaderService
  ) { }

  ngOnInit(): void {

    this._loaderService.isLoading
      .subscribe(loading => this.isLoading = loading);

    (async () => {
      const companies: Company[] = await this._companyService.getCompanies(this.page, this.limit, "", this.data.types);
      const selectableCompanies: SelectableCompany[] = companies.map(company => ({ company, selected: false }))
      this.companiesDataSource = new MatTableDataSource<SelectableCompany>(selectableCompanies);

      const data: any = await this._sharedService
        .getFormData(['company_type']);

      // loading has finished 

      this.companyTypes = data ? data.company_type : [];
      this.companiesDataSource.sort = this.sort;
      this.totalNumberOfCompanies = this._companyService
        .getTotalNumberOfCompanies();
      this.page = 1;
      this.initialized = true;
    })();

    this.companyNameFilter
      .valueChanges
      .subscribe(value => {
        this.page = 1;
        this.listCompanies(
          this.page,
          this.limit,
          value
        )
      });
  }

  onPageChange(event: PageEvent) {
    // Get more batches 
    this.limit = event.pageSize;
    this.page = event.pageIndex + 1;

    this.listCompanies(
      this.page,
      this.limit,
      this.companyNameFilter.value,
      this.data.types
    )
  }

  listCompanies(
    page: number, 
    limit: number, 
    search: string, 
    types: string | null = null) {
    (async () => {
      const newCompanies = await this._companyService
        .getCompanies(this.page, this.limit, search, types);

      const selectableCompanies: SelectableCompany[] = newCompanies.map(company => ({ company, selected: false }))
      this.companiesDataSource.data = selectableCompanies;

      this.totalNumberOfCompanies = this._companyService
        .getTotalNumberOfCompanies();

    })();
  }


  formatTime(time: string | null) {
    if (!time) return '-';
    return formatDistance(new Date(time), new Date(), { addSuffix: true });
  }

  async selectCompany(selectableCompany: SelectableCompany) {
    if (this.selectedCompany) {
      // find the previously selected company a deselect it 
      const previousSelectableCompany: SelectableCompany | undefined = this.companiesDataSource
        .data
        .find(selectableCompany => {
          if (!this.selectedCompany) return false;
          return selectableCompany.company.id === this.selectedCompany.id
        })

      if (previousSelectableCompany) previousSelectableCompany.selected = false;
    }

    this.selectedCompany = selectableCompany.company;
    selectableCompany.selected = true
    // Get detailed information about the company
    if (this.selectedCompany) {
      // get more detailed information with the address
      this.selectedCompany = await this._companyService.getCompany(this.selectedCompany.id as number);
      // Mark individual selected
      selectableCompany.selected = this.selectedCompany ? true : false;
    }
  }

  getType(company: Company): string {
    const companyType: CompanyType | undefined = this.companyTypes
      .find(type => type.id === company.company_type_id);
    return companyType ? companyType.type : '-';
  }

  

}
